import { useCallback, useMemo, useRef, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { useProductsList } from "~/features/admin/products/hooks";
import { toProductRowList } from "~/features/admin/products/utils/transformers";
import { v2Route } from "~/v2/routes";
import { ProductsPresenter } from "./products.presenter";

const DEFAULT_PAGE_SIZE = 20;

export function ProductsContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.admin.products);
  const { data, refetch } = useProductsList();

  const rows = useMemo(() => toProductRowList(data.products), [data.products]);

  // The text input is driven by local state for instant feedback; the URL is a
  // background mirror (sharable / restored on reload + back-forward). We adopt the
  // URL value into local state only on a genuine external change (back/forward,
  // shared link), so our own writes never clobber the in-progress value.
  const urlSearch = params.search ?? "";
  const [search, setSearch] = useState(urlSearch);
  const lastUrlSearchRef = useRef(urlSearch);
  if (urlSearch !== lastUrlSearchRef.current) {
    lastUrlSearchRef.current = urlSearch;
    if (urlSearch !== search) setSearch(urlSearch);
  }

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedProduct = params.selectedProduct ?? null;

  const onSearchChange = useCallback(
    (next: string) => {
      setSearch(next);
      setParams((prev) => ({ ...prev, search: next ? next : undefined, page: undefined }));
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next })),
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) =>
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      })),
    [setParams],
  );

  const onRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const onSelect = useCallback((id: string) => setParams((prev) => ({ ...prev, selectedProduct: id })), [setParams]);
  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedProduct: undefined })),
    [setParams],
  );

  return (
    <ProductsPresenter
      rows={rows}
      search={search}
      onSearchChange={onSearchChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onRefresh={onRefresh}
      selectedProduct={selectedProduct}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
    />
  );
}
